import React, { MouseEvent, KeyboardEvent } from "react";
import styles from "./machines-cancel-operator-technician-modal.module.scss";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IndicatorText } from "@src/components/atoms/indicator-text/indicator-text";
import companyIcon from "@src/assets/svg/icon-building.svg";
import dbIcon from "@src/assets/svg/source-icon.svg";
import closeIcon from "@src/assets/svg/close-icon.svg";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { MachinesListSupportSessionsCancelRequestModalTextTypes } from "@common/types/machines-list-types";
import userIcon from "@src/assets/svg/user-profile-icon.svg";
import IconDisconnectSupportTechnician from "@src/assets/svg/icon-disconnect-support-technician.svg";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";

const cssClassName = "machines-cancel-operator-technician-modal";

interface MachinesCancelOperatorTechnicianManagerModalPropsTypes {
  onConfirm: (e: MouseEvent | KeyboardEvent) => void;
  onCancel: (e: MouseEvent | KeyboardEvent) => void;
  machineName: string;
  companyName: string;
  isConnected: boolean;
  modalTexts: MachinesListSupportSessionsCancelRequestModalTextTypes;
  loading: boolean;
  displayName: string;
  disableConfirmAction?: boolean;
  disabledConfirmTooltip?: string;
}

export const MachinesCancelOperatorTechnicianManagerModal = ({
  onConfirm,
  onCancel,
  machineName,
  companyName,
  isConnected,
  modalTexts: texts,
  loading,
  displayName,
  disableConfirmAction = false,
  disabledConfirmTooltip = "",
}: MachinesCancelOperatorTechnicianManagerModalPropsTypes) => {
  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel(e);
    }
  };

  const handleBackdropKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      if (e.target === e.currentTarget) {
        onCancel(e);
      }
    }
  };

  return (
    <div
      className={styles[cssClassName]}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
    >
      <div className={styles[`${cssClassName}__container`]}>
        <div className={styles[`${cssClassName}__header`]}>
          <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>

          <div className={styles[`${cssClassName}__close-btn`]}>
            <IconButton fun={onCancel} icon={closeIcon} />
          </div>
        </div>

        <HorizontalSeparator verticalMargin={12} />

        <div className={styles[`${cssClassName}__content`]}>
          <p className={styles[`${cssClassName}__message`]}>{texts.message}</p>

          <div className={styles[`${cssClassName}__info-card`]}>
            <div className={styles[`${cssClassName}__user-row`]}>
              <img
                className={styles[`${cssClassName}__icon-user`]}
                src={userIcon}
                alt="User"
              />
              <span className={styles[`${cssClassName}__user-name`]}>
                {displayName}
              </span>
            </div>
            <div className={styles[`${cssClassName}__company-row`]}>
              <img
                className={styles[`${cssClassName}__icon-company`]}
                src={companyIcon}
                alt="Company"
              />
              <span className={styles[`${cssClassName}__info-text-company`]}>
                {companyName}
              </span>
            </div>
            <div className={styles[`${cssClassName}__disconnect-icon-row`]}>
              <img
                className={styles[`${cssClassName}__icon-disconnect`]}
                src={IconDisconnectSupportTechnician}
                alt="Disconnect Support Technician"
              />
            </div>
            <div className={styles[`${cssClassName}__machine-row`]}>
              <IndicatorText text="" variant={isConnected ? "green" : "red"} />
              <img
                className={styles[`${cssClassName}__icon-db`]}
                src={dbIcon}
                alt="Machine"
              />
              <span className={styles[`${cssClassName}__info-text`]}>
                {machineName}
              </span>
            </div>
          </div>
        </div>

        <HorizontalSeparator verticalMargin={12} />

        <div className={styles[`${cssClassName}__actions`]}>
          <FlexRowContainer horizontalGap={12}>
            <ButtonWithLoader
              fun={onConfirm}
              text={texts.confirmButton}
              variant={"dark-orange"}
              loading={loading}
              disabled={disableConfirmAction}
              tooltip={disabledConfirmTooltip}
              style={disableConfirmAction ? { opacity: 0.4 } : undefined}
            />
            <Button
              fun={onCancel}
              text={texts.cancelButton}
              variant="secondary"
            />
          </FlexRowContainer>
        </div>
      </div>
    </div>
  );
};
